import { useState } from "react";
import { ConnectedShiftFormDialog, type ShiftFormRequest } from "../../shifts/ConnectedShiftFormDialog";
import type { OtsBrand, OtsColorSchemePreference } from "../../theme/OtsTheme";
import {
  type ActivityShiftActionId,
  type DriverActivityAction,
  type DriverActivityActionProvider,
} from "../driver-activity-actions";
import {
  type ActivityEntry,
  type ActivityTicketDraft,
  type ActivityTicketKind,
  nextShiftDraft,
  sharedShift,
  shiftDraftFromEntries,
  shiftDraftFromShift,
  ticketDraftFromEntries,
} from "../driver-activity-view-model";

export type UseDriverActivityCreateToolsOptions = {
  /** The region new shifts are created in when the selection does not say. */
  regionId?: string;
  /** Whether this person holds a permission; everything is offered when omitted. */
  can?: (permission: string) => boolean;
  /**
   * Opens the host's ticket form with a ticket, pickup, or delivery prefilled from the selection. Without it the
   * workspace offers shifts only.
   */
  onCreateTicket?: (draft: ActivityTicketDraft, entries: ActivityEntry[]) => void;
  /** Opens the host's ticket form to give an unmatched pickup its delivery, or a delivery its pickup. */
  onCompleteTicket?: (entry: ActivityEntry) => void;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
};

const isUnmatched = (kind: "pickup" | "delivery") => (entries: ActivityEntry[]) =>
  entries.length === 1 && entries[0].kind === kind && !entries[0].tta;

const prefillNote = (entries: ActivityEntry[]) =>
  entries.length ? `Prefilled from ${entries.length === 1 ? "the selected row" : `${entries.length} selected rows`}.` : undefined;

/**
 * New shifts and tickets, prefilled from the selected rows, and edits to a shift. Shifts are made in a dialog and
 * join the day as soon as they are saved; tickets open the host's ticket form.
 */
export const useDriverActivityCreateTools = ({
  regionId,
  can = () => true,
  onCreateTicket,
  onCompleteTicket,
  brand,
  colorScheme,
}: UseDriverActivityCreateToolsOptions = {}): DriverActivityActionProvider => {
  const [shiftRequest, setShiftRequest] = useState<ShiftFormRequest>();

  const createTicket = (kind: ActivityTicketKind) => (entries: ActivityEntry[]) =>
    onCreateTicket?.(ticketDraftFromEntries(entries, kind), entries);

  type Definition = { action: DriverActivityAction; permission: string; run: (targets: ActivityEntry[]) => void; offered?: boolean };
  const definitions: Definition[] = [
    {
      action: { id: "new-shift", label: "New Shift", scope: "optional", group: "create" },
      permission: "shift_create",
      run: (targets) => setShiftRequest({ draft: { regionId, ...shiftDraftFromEntries(targets) }, description: prefillNote(targets) }),
    },
    {
      action: { id: "new-ticket", label: "New Ticket", scope: "optional", group: "create" },
      permission: "transporttransportassoc_create", offered: !!onCreateTicket, run: createTicket("ticket"),
    },
    {
      action: { id: "new-pickup", label: "New Pickup", scope: "optional", group: "create" },
      permission: "transport_create", offered: !!onCreateTicket, run: createTicket("pickup"),
    },
    {
      action: { id: "new-delivery", label: "New Delivery", scope: "optional", group: "create" },
      permission: "transport_create", offered: !!onCreateTicket, run: createTicket("delivery"),
    },
    {
      action: { id: "add-delivery", label: "Add Delivery", scope: "single", group: "create", isVisible: isUnmatched("pickup") },
      permission: "transporttransportassoc_create", offered: !!onCompleteTicket,
      run: ([entry]) => entry && onCompleteTicket?.(entry),
    },
    {
      action: { id: "add-pickup", label: "Add Pickup", scope: "single", group: "create", isVisible: isUnmatched("delivery") },
      permission: "transporttransportassoc_create", offered: !!onCompleteTicket,
      run: ([entry]) => entry && onCompleteTicket?.(entry),
    },
    {
      action: {
        id: "edit-shift", label: "Edit Shift", scope: "any", group: "tools",
        isEnabled: (targets) => !!sharedShift(targets),
      },
      permission: "shift_edit",
      run: (targets) => {
        const shift = sharedShift(targets);
        if (shift) setShiftRequest({ draft: shiftDraftFromShift(shift) });
      },
    },
  ];
  const offered = definitions.filter((definition) => definition.offered !== false && can(definition.permission));

  const shiftActionIds = ([
    ["edit-shift", "shift_edit"],
    ["new-shift", "shift_create"],
  ] as const).filter(([, permission]) => can(permission)).map(([id]) => id as ActivityShiftActionId);

  return {
    actions: offered.map((definition) => definition.action),
    handleAction: (actionId, targets) => {
      const definition = offered.find((candidate) => candidate.action.id === actionId);
      if (!definition) return false;
      definition.run(targets);
      return true;
    },
    shiftActions: {
      ids: shiftActionIds,
      handle: (actionId, { shift, entries: shiftEntries }) => {
        if (actionId === "edit-shift" && shift) setShiftRequest({ draft: shiftDraftFromShift(shift) });
        if (actionId === "new-shift") {
          setShiftRequest(shift
            ? { draft: nextShiftDraft(shift), description: "Starts when the shift above it ended." }
            : { draft: { regionId, ...shiftDraftFromEntries(shiftEntries) }, description: "Covers the activity recorded outside a shift." });
        }
      },
    },
    dialogs: (
      <ConnectedShiftFormDialog
        request={shiftRequest}
        onClose={() => setShiftRequest(undefined)}
        regionId={regionId}
        brand={brand}
        colorScheme={colorScheme}/>
    ),
  };
};
